import { useEffect, useState } from 'react';
import { Building2, Users, MonitorSmartphone, ClipboardCheck, ArrowUpRight, Activity, AlertTriangle, Clock3, FileWarning, Wrench } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { MeshFleetSummaryDto, OperationsDashboardDto, OverviewDto, SystemStatusMode } from '../models';
import { getApiError, getMeshCentralFleetSummary, getOperationsDashboard, getOverview, getSystemMaintenance } from '../services/api';
import { formatCount } from '../utils/formatCount';
import { Button } from '../components/ui/button';
import { SystemStatusChangeModal } from '../components/SystemStatusChangeModal';

function Stat({ icon: Icon, label, value, sub, accent }: { icon: typeof Activity; label: string; value: string | number; sub: string; accent?: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="mt-4 font-mono text-2xl font-semibold tracking-tight text-foreground">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
      <p className="mt-2 text-xs text-muted-foreground/70">{sub}</p>
    </div>
  );
}

export const OverviewPage = () => {
  const [data, setData] = useState<OverviewDto>();
  const [operations, setOperations] = useState<OperationsDashboardDto>();
  const [fleet, setFleet] = useState<MeshFleetSummaryDto>();
  const [fleetError, setFleetError] = useState('');
  const [systemStatusMode, setSystemStatusMode] = useState<SystemStatusMode>('normal');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [requestedStatusMode, setRequestedStatusMode] = useState<SystemStatusMode>('normal');
  const [error, setError] = useState('');
  useEffect(() => {
    Promise.all([getOverview(), getOperationsDashboard(), getSystemMaintenance()])
      .then(([overview, ops, maintenance]) => {
        setData(overview.data.data);
        setOperations(ops.data.data);
        setSystemStatusMode(maintenance.data.data.mode);
      })
      .catch((e) => setError(getApiError(e)));
    getMeshCentralFleetSummary()
      .then((response) => setFleet(response.data.data))
      .catch((e) => setFleetError(getApiError(e)));
  }, []);

  const openStatusModal = (mode: SystemStatusMode) => {
    setRequestedStatusMode(mode);
    setStatusModalOpen(true);
  };

  const availability = fleet?.total ? Math.round((fleet.online / fleet.total) * 100) : 0;

  return (
    <AppLayout title="Обзор">
      <PageHeader title="Обзор платформы" description="Оперативное состояние клиентов, пользователей и парка терминалов." />
      {error && <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {!data && !error ? <LoadingState /> : data ? (
        <div className="space-y-6">
          <div className={`flex flex-col gap-4 rounded-xl border p-5 sm:flex-row sm:items-center sm:justify-between ${systemStatusMode === 'incident' ? 'border-destructive/40 bg-destructive/10' : systemStatusMode === 'maintenance' ? 'border-warning/40 bg-warning/10' : 'border-border bg-card'}`}>
            <div className="flex items-start gap-3">
              <div className={`rounded-lg p-2 ${systemStatusMode === 'incident' ? 'bg-destructive/20 text-destructive' : systemStatusMode === 'maintenance' ? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'}`}>
                {systemStatusMode === 'incident' ? <AlertTriangle className="h-5 w-5" /> : <Wrench className="h-5 w-5" />}
              </div>
              <div>
                <h2 className="font-bold">Глобальное состояние системы</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {systemStatusMode === 'maintenance' ? 'Включена заглушка технических работ.' : systemStatusMode === 'incident' ? 'Включена заглушка сбоя системы.' : 'Система работает в обычном режиме.'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => openStatusModal('normal')} disabled={systemStatusMode === 'normal'}>Нормальный режим</Button>
              <Button variant="default" onClick={() => openStatusModal('maintenance')} disabled={systemStatusMode === 'maintenance'}>Включить техработы</Button>
              <Button variant="destructive" onClick={() => openStatusModal('incident')} disabled={systemStatusMode === 'incident'}>Включить сбой</Button>
            </div>
          </div>

          <SystemStatusChangeModal
            open={statusModalOpen}
            currentMode={systemStatusMode}
            initialMode={requestedStatusMode}
            onClose={() => setStatusModalOpen(false)}
            onChanged={setSystemStatusMode}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat icon={Building2} label="Клиенты" value={data.tenantCount} sub={formatCount(data.tenantCount, ['организация', 'организации', 'организаций'])} accent />
            <Stat icon={Users} label="Пользователи" value={data.userCount} sub={formatCount(data.userCount, ['учётная запись', 'учётные записи', 'учётных записей'])} />
            <Stat icon={MonitorSmartphone} label="Терминалы" value={fleet ? `${fleet.online}/${fleet.total}` : '—'} sub="MeshCentral: онлайн / всего" accent />
            <Stat icon={ClipboardCheck} label="Осмотры" value={data.medicalCheckupsToday} sub="за сегодня" />
          </div>

          {operations && (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div><h2 className="font-bold">Поток медосмотров</h2><p className="text-xs text-muted-foreground">Текущее состояние обработки и технические исключения</p></div>
                <span className="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-primary">Live</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {[
                  { label: 'В очереди', value: operations.new_count, icon: Clock3, tone: 'text-primary bg-accent' },
                  { label: 'На подписании', value: operations.signing_count, icon: ClipboardCheck, tone: 'text-warning bg-warning/10' },
                  { label: 'Ошибки подписи', value: operations.error_count, icon: AlertTriangle, tone: 'text-destructive bg-destructive/10' },
                  { label: 'Без шаблона', value: operations.tenants_without_template, icon: FileWarning, tone: 'text-destructive bg-destructive/10' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-sm">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${item.tone}`}><item.icon className="h-5 w-5" /></div>
                    <div><p className="text-2xl font-extrabold">{item.value}</p><p className="text-xs text-muted-foreground">{item.label}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6">
            {/* Terminal status */}
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Состояние терминалов</h2>
                  <p className="text-xs text-muted-foreground">Фактическая доступность устройств в MeshCentral</p>
                </div>
                <span className="flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 font-mono text-xs text-muted-foreground">
                  <Activity className="h-3.5 w-3.5 text-primary" /> {availability}% в сети
                </span>
              </div>

              <div className="mt-6">
                {fleetError && <p className="mb-3 text-sm text-destructive">MeshCentral: {fleetError}</p>}
                <div className="mb-1 flex items-baseline gap-2">
                  <span className="font-mono text-3xl font-bold text-foreground">{availability}%</span>
                  <span className="text-sm text-muted-foreground">доступность</span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full rounded-full transition-all ${availability >= 80 ? 'bg-success' : 'bg-primary'}`}
                    style={{ width: `${availability}%` }}
                  />
                </div>
              </div>

              <div className="mt-4 flex gap-6">
                <div>
                  <p className="text-xs text-muted-foreground">В сети</p>
                  <p className="font-bold text-success">{fleet?.online ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Не в сети</p>
                  <p className="font-bold text-destructive">{fleet?.offline ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Всего</p>
                  <p className="font-bold text-foreground">{fleet?.total ?? '—'}</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      ) : null}
    </AppLayout>
  );
};
